import {useState} from 'react';
import {Button, Card, Form, InputGroup} from 'react-bootstrap';
import {useIntl} from 'react-intl';
import {L2tpParams} from '../../Types/routerForms';
import {useUpdateL2tp} from '../../Hooks/RouterManager';
import {randomStrongPassword} from '../../Utils/random';
import Level3Title from '../../../../app/common/components/title/Level3Title';

const isStrongSecret = (s: string): boolean =>
  s.length >= 8 && /[A-Z]/.test(s) && /[a-z]/.test(s) && /\d/.test(s);

interface L2tpParamsSectionProps {
  routerId: string;
  params: L2tpParams;
}

const L2tpParamsSection = ({routerId, params}: L2tpParamsSectionProps): JSX.Element => {
  const intl = useIntl();
  const [draft, setDraft] = useState<L2tpParams>(params);
  const [showSecret, setShowSecret] = useState(false);
  const [validated, setValidated] = useState(false);
  const updateL2tp = useUpdateL2tp(routerId);

  const isSecretValid = isStrongSecret(draft.ipsecSecret);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidated(true);
    if (!isSecretValid) return;
    updateL2tp.mutate({active: draft.active, ipsecSecret: draft.ipsecSecret});
  };

  return (
    <Card className="mb-4">
      <Card.Header>
        <Card.Title>
          <Level3Title title={intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VPN.PARAMS_TITLE'})} />
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <Form.Group>
                <Form.Label>
                  {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VPN.STATUS'})}
                </Form.Label>
                <Form.Select
                  value={draft.active ? 'on' : 'off'}
                  onChange={(e) => setDraft({...draft, active: e.target.value === 'on'})}
                >
                  <option value="on">
                    {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VPN.STATUS_ON'})}
                  </option>
                  <option value="off">
                    {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VPN.STATUS_OFF'})}
                  </option>
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label className="required">
                  {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VPN.IPSEC_SECRET'})}
                </Form.Label>
                <InputGroup hasValidation>
                  <Form.Control
                    type={showSecret ? 'text' : 'password'}
                    value={draft.ipsecSecret}
                    onChange={(e) => setDraft({...draft, ipsecSecret: e.target.value})}
                    isInvalid={validated && !isSecretValid}
                    required
                  />
                  <Button
                    variant="outline-secondary"
                    type="button"
                    onClick={() => setShowSecret((s) => !s)}
                  >
                    <i className={`fa-solid ${showSecret ? 'fa-eye-slash' : 'fa-eye'}`} />
                  </Button>
                  <Button
                    variant="outline-secondary"
                    type="button"
                    title={intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VPN.RANDOM_PWD'})}
                    onClick={() => setDraft({...draft, ipsecSecret: randomStrongPassword(12)})}
                  >
                    <i className="fa-solid fa-dice" />
                  </Button>
                  <Form.Control.Feedback type="invalid">
                    {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VPN.SECRET_HELP'})}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </div>
            <div className="col-md-3">
              <Button variant="primary" type="submit">
                {intl.formatMessage({id: 'COMMON.SAVE'})}
              </Button>
            </div>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default L2tpParamsSection;
